import React, { useEffect, useState } from "react";
import { View, FlatList, SafeAreaView } from "react-native";
import {
  Error,
  Empty,
  Button,
  Header,
  Loading,
  AnimatedInfinityList,
} from "components";
import CustomerReportFilters, { Filters } from "./CustomerReportFilters";
import CustomerReportRow from "./CustomerReportRow";

import css from "styles";
import { useFlag } from "hooks";
import { sizes, strings } from "assets/constants";

import { useCustomerReport } from "clients/apis";
import ExportExcel from "./ExportExcel";
import { icons } from "assets/icons";
import SelectedFiltersView from "./SelectedFiltersView";
import { useIsFocused } from "@react-navigation/native";
import GetAllCustomers from "screens/home/GetAllCustomers";

const initialFilters: Filters = {
  mobile: "",
  customerTypes: undefined,
  friendships: [],
  cities: [],
  popularGroups: undefined,
  customerGroups: [],
  colleagueCompanies: [],
  creators: [],
};

export default function CustomerReport() {
  const isFocused = useIsFocused();
  const { flg: filter, on: showFilter, off: hideFilter } = useFlag();

  const [filters, setFilters] = useState(initialFilters);

  const {
    data,
    error,
    isError,
    refetch,
    isLoading,
    hasNextPage,
    isRefetching,
    fetchNextPage,
    isFetchingNextPage,
  } = useCustomerReport({
    mobile: filters?.mobile,
    pagination: 25,
    customer_types:
      filters?.customerTypes?.id === null ? "null" : filters?.customerTypes?.id,
    popular_groups:
      filters?.popularGroups?.id === null ? "null" : filters?.popularGroups?.id,
    friendships: filters?.friendships?.map((f) =>
      f?.id === null ? "null" : f?.id
    ),
    customer_groups: filters?.customerGroups?.map((c) =>
      c?.id === null ? "null" : c?.id
    ),
    colleague_companies: filters?.colleagueCompanies?.map((co) =>
      co?.id === null ? "null" : co?.id
    ),
    creators: filters?.creators?.map((c) => (c?.id === null ? "null" : c?.id)),
    cities: filters?.cities?.filter((e) => e?.id)?.map((e) => e?.id),
    noAddress: filters?.cities?.some((e) => e.id === null) ? "true" : undefined,
  });

  useEffect(() => {
    isFocused && refetch();
  }, [isFocused]);

  const _renderItem = (items: []) => {
    return (
      <>
        {items.map((customer: CustomerT, key: number) => {
          return <CustomerReportRow key={String(key)} customer={customer} />;
        })}
      </>
    );
  };

  const _renderContent = () => {
    if (isLoading) {
      return <Loading />;
    }

    if (isError) {
      return <Error error={error} reset={refetch} loading={isRefetching} />;
    }

    if (data?.pages?.[0]?.data?.data?.length === 0) {
      return (
        <Empty
          icon={{ name: "user" }}
          style={{ marginTop: 70 }}
          message={strings.no_customer}
        />
      );
    }

    return (
      <>
        <AnimatedInfinityList
          onRefresh={refetch}
          renderItemList={_renderItem}
          responseData={data}
          fetchNextPage={fetchNextPage}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          paddingBottom={70}
          paddingTop={sizes.space / 3}
        />
        <View style={{ position: "absolute", bottom: 10, left: 10 }}>
          <ExportExcel filters={filters} />
          <GetAllCustomers />
        </View>
      </>
    );
  };

  return (
    <>
      <Header title={strings.customer_reports} />

      <SafeAreaView style={{ ...css.container, ...css.screenBg }}>
        <View
          style={{
            ...css.rRowCenterAlign,
            height: 75,
            paddingHorizontal: 12,
          }}
        >
          <Button
            title={{ value: strings.filters }}
            onPress={showFilter}
            width={140}
            imgIcon={{ icon: icons._filter }}
          />

          <FlatList
            contentContainerStyle={{ flexGrow: 1 }}
            inverted
            horizontal
            data={["_"]}
            renderItem={(_) => {
              return (
                <>
                  {filters?.mobile?.length > 0 && (
                    <SelectedFiltersView
                      title={strings.mobile}
                      values={[filters?.mobile]}
                    />
                  )}

                  {filters?.customerTypes?.title?.length > 0 && (
                    <SelectedFiltersView
                      title={strings.customer_type}
                      values={[filters?.customerTypes?.title]}
                    />
                  )}

                  {filters?.customerGroups?.length > 0 && (
                    <SelectedFiltersView
                      title={strings.customer_category}
                      values={filters?.customerGroups?.map((e) => e.title)}
                    />
                  )}

                  {filters?.friendships?.length > 0 && (
                    <SelectedFiltersView
                      title={strings.familiarity_way}
                      values={filters?.friendships?.map((e) => e.title)}
                    />
                  )}

                  {filters?.cities?.length > 0 && (
                    <SelectedFiltersView
                      title={strings.city}
                      values={filters?.cities?.map((e) => e.text)}
                    />
                  )}

                  {filters?.creators?.length > 0 && (
                    <SelectedFiltersView
                      title={strings.creator}
                      values={filters?.creators?.map((e) => e.name)}
                    />
                  )}

                  {filters?.colleagueCompanies?.length > 0 && (
                    <SelectedFiltersView
                      title={strings.co_workers}
                      values={filters?.colleagueCompanies?.map((e) => e.name)}
                    />
                  )}

                  {filters?.popularGroups?.title?.length > 0 && (
                    <SelectedFiltersView
                      title={strings.popular_category}
                      values={[filters?.popularGroups?.title]}
                    />
                  )}
                </>
              );
            }}
          />
          <View style={{ flex: 1 }} />
        </View>

        {_renderContent()}
      </SafeAreaView>
      {filter && (
        <CustomerReportFilters
          value={filters}
          onApply={setFilters}
          close={hideFilter}
        />
      )}
    </>
  );
}
